'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  addFavouriteAPI,
  getFavouritesAPI,
  removeFavouriteAPI,
} from '@/helpers/api/favourites-api';
import { useUser } from '@/providers/UserProvider';
import { useProfileSlider } from '@/providers/ProfileSliderProvider';
import { IGtmParameters, useSendGtmEvent } from '@/hooks/use-send-gtm-event';

export type FavouriteType =
  | 'offer'
  | 'shop'
  | 'foodAndBeverage'
  | 'entertainment'
  | 'event'
  | 'service';
// | 'leisurePackageOffer' // TODO: re-enable when LPO favourites are needed

export interface FavouriteItem {
  id: string;
  type: FavouriteType;
}

interface FavouritesContextType {
  favourites: FavouriteItem[];
  toggleFavourite: (id: string, type: FavouriteType, name?: string) => void;
  isFavourite: (id: string) => boolean;
  loadFavourites: () => Promise<void>;
  mpId?: string;
  locale?: string;
}

interface FavouritesProviderProps {
  children: React.ReactNode;
  mpId?: string;
  locale?: string;
  config?: Record<string, string>;
}

const FavouritesContext = createContext<FavouritesContextType | undefined>(
  undefined,
);

export const FavouritesProvider = ({
  children,
  mpId,
  locale,
  config,
}: FavouritesProviderProps) => {
  const { user } = useUser();
  const [favourites, setFavourites] = useState<FavouriteItem[]>([]);
  const favouritesRef = useRef<FavouriteItem[]>([]);
  const { openSlider } = useProfileSlider();
  const isFavouritesEnabled =
    config?.NEXT_PUBLIC_ICIM_LOGIN_ENABLED === 'true' &&
    config?.NEXT_PUBLIC_IS_MP_LIVLI_FAMILY === 'true';

  useEffect(() => {
    favouritesRef.current = favourites;
  }, [favourites]);

  const toggleFavourite = useCallback(
    (id: string, type: FavouriteType, name?: string) => {
      if (!isFavouritesEnabled) return;
      if (!user) {
        openSlider();
        return;
      }

      const prev = favouritesRef.current;
      const isAdding = !prev.some((f) => f.id === id);
      const next = isAdding
        ? [...prev, { id, type }]
        : prev.filter((f) => f.id !== id);

      favouritesRef.current = next;
      setFavourites(next);

      if (mpId && locale) {
        if (isAdding) {
          addFavouriteAPI({
            contentfulId: id,
            itemType: type,
            mpId,
            locale,
          }).catch((err: unknown) =>
            console.error('Failed to add favourite:', err),
          );
        } else {
          removeFavouriteAPI(id).catch((err: unknown) =>
            console.error('Failed to remove favourite:', err),
          );
        }
      }
    },
    [mpId, locale, user, isFavouritesEnabled],
  );

  const isFavourite = useCallback(
    (id: string) => favourites.some((f) => f.id === id),
    [favourites],
  );

  const loadFavourites = useCallback(async (): Promise<void> => {
    if (!mpId) return;
    try {
      const items = await getFavouritesAPI(mpId);
      const mapped: FavouriteItem[] = items.map((item) => ({
        id: item.contentfulId,
        type: item.itemType as FavouriteType,
      }));
      setFavourites(mapped);
    } catch (err: unknown) {
      console.error('Failed to load favourites:', err);
    }
  }, [mpId]);

  useEffect(() => {
    if (user && isFavouritesEnabled) {
      loadFavourites();
    } else {
      setFavourites([]);
    }
  }, [user, loadFavourites, isFavouritesEnabled]);

  return (
    <FavouritesContext.Provider
      value={{
        favourites,
        toggleFavourite,
        isFavourite,
        loadFavourites,
        mpId,
        locale,
      }}
    >
      {children}
    </FavouritesContext.Provider>
  );
};

export const useFavourites = (): FavouritesContextType => {
  const context = useContext(FavouritesContext);
  if (!context) {
    return {
      favourites: [],
      toggleFavourite: () => {},
      isFavourite: () => false,
      loadFavourites: async () => {},
      mpId: undefined,
      locale: undefined,
    };
  }
  return context;
};
